import {
  RawUserData,
  UpdateUserAccountPasswordResponse,
  UpdateUserProfilePayload
} from './types'
import { authApi, managementApi } from '../../modules/servers/api'
import { createEntityAdapter, EntityState } from '@reduxjs/toolkit'

export const usersApi = managementApi.injectEndpoints({
  endpoints: (builder) => ({
    // Organisation-specific endpoints //
    getUsersInOrganisation: builder.query<
      EntityState<RawUserData, RawUserData['id']>,
      string
    >({
      query: (organisationId) => `organisations/${organisationId}/users`,
      providesTags: ['users'],
      transformResponse: (response: RawUserData[]) => {
        const entityAdapter = createEntityAdapter<RawUserData>()
        try {
          return entityAdapter.addMany(
            entityAdapter.getInitialState(),
            response
          )
        } catch (error) {
          console.error(`Error in transformResponse for users:`, error)
          throw error
        }
      }
    }),
    linkUserWithOrg: builder.mutation<
      RawUserData,
      { organisationId: string; body: { userId: string; role: string } }
    >({
      query: ({ organisationId, body }) => ({
        url: `organisations/${organisationId}/users`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['users']
    }),
    unlinkUserFromOrg: builder.mutation<
      void,
      { organisationId: string; userId: string }
    >({
      query: ({ organisationId, userId }) => ({
        url: `organisations/${organisationId}/users/${userId}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['users']
    }),

    // User-specific endpoints //
    listAllUsers: builder.query<RawUserData[], void>({
      query: () => 'users',
      providesTags: ['users']
    }),
    getUser: builder.query<RawUserData, string>({
      query: (id) => `users/${id}`,
      providesTags: (result, error, id) => [{ type: 'users', id }]
    }),

    createUser: builder.mutation<
      RawUserData,
      { organisationId: string } & Partial<RawUserData>
    >({
      query: (body) => ({
        url: 'users',
        method: 'POST',
        body
      }),
      invalidatesTags: ['users']
    }),

    updateUser: builder.mutation<
      RawUserData,
      { id: string } & UpdateUserProfilePayload
    >({
      query: ({ id, ...body }) => ({
        url: `users/${id}`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: ['users']
    }),

    updateUserOrgRole: builder.mutation<
      unknown[],
      {
        organisationId: string
        userId: string
        role: 'NoAccess' | 'Viewer' | 'Admin'
      }
    >({
      query: ({ organisationId, userId, role }) => ({
        url: `organisations/${organisationId}/users/${userId}/role`,
        method: 'PATCH',
        body: { role }
      }),
      invalidatesTags: ['users']
    }),

    deactivateUser: builder.mutation<void, string>({
      query: (id) => ({
        url: `users/${id}/deactivate`,
        method: 'POST'
      }),
      invalidatesTags: ['users']
    })
  })
})

export const userAccountApi = authApi.injectEndpoints({
  endpoints: (builder) => ({
    updateUserAccountPassword: builder.mutation<
      UpdateUserAccountPasswordResponse,
      { password?: string; displayName?: string }
    >({
      query: (body) => ({
        url: `user`,
        method: 'PUT',
        body,
        withCredentials: true
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          console.error('Error updating user:', error)
        }
      }
    })
  })
})

export const {
  useGetUsersInOrganisationQuery,
  useLinkUserWithOrgMutation,
  useUnlinkUserFromOrgMutation,
  useCreateUserMutation,
  useListAllUsersQuery,
  useGetUserQuery,
  useUpdateUserMutation,
  useUpdateUserOrgRoleMutation,
  useDeactivateUserMutation
} = usersApi

export const { useUpdateUserAccountPasswordMutation } = userAccountApi
